import { useState, useEffect, useRef, useMemo } from 'react';
import {
  Modal, ModalHeader, ModalBody, ModalFooter,
  Input, FormGroup, Label, Spinner, Button,
  Carousel, CarouselItem, CarouselControl, CarouselIndicators, CarouselCaption,
} from 'reactstrap';
import Select from 'react-select';
import FeatherIcon from 'feather-icons-react';
import { toast } from 'react-toastify';
import Swal from 'sweetalert2';
import { requestVIRUploadUrl, confirmVIRUpload, getVIRSignedUrls, getVIRVendorsByCategory } from './FormikHelpers/Api';
import { getFileExtension, imageTypes, videoTypes, pdfTypes, officeTypes } from '../../../../components/DocumentPreviewer/utils';
import { Previewer } from '../../../../components/DocumentPreviewer/Previewer';
import { formatDateDenver } from './FormikHelpers/utils';

const VIR_STATUSES = [
  { value: 'Open', label: 'Open', color: '#3B82F6' },
  { value: 'In Progress', label: 'In Progress', color: '#D97706' },
  { value: 'Completed', label: 'Completed', color: '#059669' },
  { value: 'Not Able to Complete', label: 'Not Able to Complete', color: '#DC2626' },
];

const selectStyles = {
  control: (base, { isFocused }) => ({
    ...base,
    borderColor: isFocused ? '#6366F1' : '#E2E8F0',
    borderRadius: 8,
    minHeight: 38,
    fontSize: '0.83rem',
    backgroundColor: '#FFFFFF',
    boxShadow: isFocused ? '0 0 0 2px rgba(99,102,241,0.15)' : 'none',
    '&:hover': { borderColor: '#6366F1' },
  }),
  option: (base, { isSelected, isFocused }) => ({
    ...base, fontSize: '0.83rem',
    background: isSelected ? 'rgb(85, 201, 231)' : isFocused ? '#EEF2FF' : 'white',
    color: isSelected ? 'white' : '#0F172A',
  }),
  menu: (base) => ({ ...base, borderRadius: 8, border: '1px solid #E2E8F0', boxShadow: '0 8px 24px rgba(15,23,42,0.1)', zIndex: 9999 }),
  menuPortal: (base) => ({ ...base, zIndex: 9999 }),
};

const LABEL_STYLE = {
  fontSize: '0.75rem', fontWeight: 700, color: '#334155',
  textTransform: 'uppercase', letterSpacing: '0.5px', marginBottom: 6,
};

const FieldError = ({ msg }) =>
  msg ? <div style={{ fontSize: '0.72rem', color: '#DC2626', marginTop: 4 }}>{msg}</div> : null;

const getFileTypeFromKey = (keyOrName) => {
  const ext = getFileExtension(keyOrName).toLowerCase();
  if (pdfTypes.includes(ext)) return 'pdf';
  if (imageTypes.includes(ext)) return 'image';
  if (videoTypes.includes(ext)) return 'video';
  if (officeTypes.includes(ext)) return 'office';
  return 'unknown';
};

const ReportFilePreviewModal = ({ isOpen, toggle, signedUrl, fileKey }) => {
  const type = fileKey ? getFileTypeFromKey(fileKey) : 'unknown';
  const fileName = fileKey ? fileKey.split('/').pop() : 'Report';
  return (
    <Modal isOpen={isOpen} toggle={toggle} centered size="xl" style={{ zIndex: 12000 }}>
      <ModalHeader toggle={toggle} style={{ background: '#0F172A', borderBottom: 'none', padding: '14px 20px' }}>
        <span style={{ color: 'white', fontWeight: 700, fontSize: '0.9rem' }}>{fileName}</span>
      </ModalHeader>
      <ModalBody style={{ padding: 0, background: '#0F172A' }}>
        {signedUrl ? (
          <Previewer signedUrl={signedUrl} type={type} height="70vh" width="100%" />
        ) : (
          <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'center', height: '70vh' }}>
            <Spinner style={{ color: '#A5B4FC' }} />
          </div>
        )}
      </ModalBody>
      <ModalFooter style={{ background: '#0F172A', border: 'none', padding: '12px 20px' }}>
        <Button color="secondary" onClick={toggle}>Close</Button>
        {signedUrl && (
          <a
            href={signedUrl}
            target="_blank"
            rel="noopener noreferrer"
            download={fileName}
            className="it-btn it-btn--primary-solid"
            style={{ textDecoration: 'none' }}
          >
            <FeatherIcon icon="download" size={14} style={{ marginRight: 4 }} />
            Download
          </a>
        )}
      </ModalFooter>
    </Modal>
  );
};

const AttachmentPreviewModal = ({ isOpen, toggle, items, initialIndex = 0 }) => {
  const [activeIndex, setActiveIndex] = useState(initialIndex);
  const [animating, setAnimating] = useState(false);

  useEffect(() => { if (isOpen) setActiveIndex(initialIndex); }, [isOpen, initialIndex]);

  const next = () => { if (!animating) setActiveIndex((p) => (p === items.length - 1 ? 0 : p + 1)); };
  const prev = () => { if (!animating) setActiveIndex((p) => (p === 0 ? items.length - 1 : p - 1)); };

  if (!isOpen || !items?.length) return null;

  return (
    <Modal isOpen={isOpen} toggle={toggle} centered size="xl" style={{ zIndex: 10000 }}>
      <ModalHeader toggle={toggle} style={{ background: '#0F172A', borderBottom: 'none', padding: '14px 20px' }}>
        <div style={{ display: 'flex', alignItems: 'center', gap: 8 }}>
          <FeatherIcon icon="image" size={18} color="#A5B4FC" />
          <span style={{ color: 'white', fontWeight: 700, fontSize: '0.9rem' }}>
            Preview ({activeIndex + 1} / {items.length})
          </span>
        </div>
      </ModalHeader>
      <ModalBody style={{ padding: 0, background: '#0F172A' }}>
        <Carousel activeIndex={activeIndex} next={next} previous={prev} interval={false} pause={false}>
          <CarouselIndicators
            items={items} activeIndex={activeIndex}
            onClickHandler={(i) => { if (!animating) setActiveIndex(i); }}
          />
          {items.map((item, idx) => (
            <CarouselItem key={idx} onExiting={() => setAnimating(true)} onExited={() => setAnimating(false)}>
              <div style={{ textAlign: 'center', minHeight: '60vh', display: 'flex', alignItems: 'center', justifyContent: 'center' }}>
                {item.type === 'video' ? (
                  <video src={item.src} controls autoPlay
                    style={{ maxHeight: '70vh', maxWidth: '100%', objectFit: 'contain', borderRadius: 8 }} />
                ) : item.type === 'image' ? (
                  <img src={item.src} alt={item.caption}
                    style={{ maxHeight: '70vh', maxWidth: '100%', objectFit: 'contain', borderRadius: 8 }} />
                ) : (
                  item.src ? (
                    <Previewer signedUrl={item.src} type={item.type} height="60vh" width="100%" />
                  ) : (
                    <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'center', height: '60vh' }}>
                      <Spinner style={{ color: '#A5B4FC' }} />
                    </div>
                  )
                )}
              </div>
              <CarouselCaption captionText={item.caption} captionHeader=""
                style={{ background: 'rgba(15,23,42,0.75)', borderRadius: 8 }} />
            </CarouselItem>
          ))}
          <CarouselControl direction="prev" directionText="Previous" onClickHandler={prev} />
          <CarouselControl direction="next" directionText="Next" onClickHandler={next} />
        </Carousel>
      </ModalBody>
      <ModalFooter style={{ background: '#0F172A', border: 'none', padding: '12px 20px' }}>
        <Button color="secondary" onClick={toggle}>Close</Button>
      </ModalFooter>
    </Modal>
  );
};

const ThumbnailGrid = ({ uploadedKeys, pendingFiles, signedUrlCache, onPreview, onRemoveUploaded, onRemovePending, canEdit, disabled }) => (
  <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fill, minmax(88px, 1fr))', gap: 8, marginTop: 10 }}>
    {uploadedKeys.map((key, i) => {
      const url = signedUrlCache[key];
      const type = getFileTypeFromKey(key);
      const isImage = type === 'image';
      const isVideo = type === 'video';
      const fileName = key.split('/').pop();
      return (
        <div
          key={`up-${i}`}
          onClick={() => url && onPreview(i)}
          style={{ position: 'relative', height: 88, borderRadius: 8, overflow: 'hidden', cursor: url ? 'pointer' : 'default', border: '2px solid #E2E8F0', background: '#F1F5F9' }}
        >
          {url ? (
            isVideo ? (
              <div style={{ height: '100%', background: 'linear-gradient(135deg,#667eea,#764ba2)', display: 'flex', alignItems: 'center', justifyContent: 'center' }}>
                <FeatherIcon icon="play-circle" size={28} color="white" />
              </div>
            ) : isImage ? (
              <img src={url} alt={fileName} style={{ width: '100%', height: '100%', objectFit: 'cover' }} />
            ) : (
              <div style={{ height: '100%', background: '#EEF2FF', display: 'flex', alignItems: 'center', justifyContent: 'center' }}>
                <FeatherIcon icon="file-text" size={24} color="#4F46E5" />
              </div>
            )
          ) : (
            <div style={{ height: '100%', display: 'flex', alignItems: 'center', justifyContent: 'center' }}>
              <Spinner size="sm" style={{ color: '#4F46E5' }} />
            </div>
          )}
          <div style={{ position: 'absolute', bottom: 0, left: 0, right: 0, background: 'linear-gradient(to top,rgba(0,0,0,0.65),transparent)', padding: '4px 5px' }}>
            <span style={{ color: 'white', fontSize: '0.62rem', display: 'block', whiteSpace: 'nowrap', overflow: 'hidden', textOverflow: 'ellipsis' }}>{fileName}</span>
          </div>
          <div style={{ position: 'absolute', top: 4, left: 4 }}>
            <span style={{ background: '#059669', color: 'white', borderRadius: 4, padding: '1px 5px', fontSize: '0.58rem', fontWeight: 700 }}>✓</span>
          </div>
          {canEdit && (
            <button
              type="button" disabled={disabled}
              onClick={(e) => { e.stopPropagation(); onRemoveUploaded(i); }}
              style={{ position: 'absolute', top: 4, right: 4, width: 22, height: 22, borderRadius: '50%', background: '#DC2626', border: 'none', cursor: 'pointer', display: 'flex', alignItems: 'center', justifyContent: 'center', boxShadow: '0 1px 4px rgba(0,0,0,0.35)' }}
            >
              <FeatherIcon icon="trash-2" size={11} color="white" />
            </button>
          )}
        </div>
      );
    })}
    {pendingFiles.map((f, i) => {
      const type = getFileTypeFromKey(f.name);
      const isImage = f.type?.startsWith('image/') || type === 'image';
      const isVideo = f.type?.startsWith('video/') || type === 'video';
      return (
        <div
          key={`pend-${i}`}
          onClick={() => f.preview && onPreview(uploadedKeys.length + i)}
          style={{ position: 'relative', height: 88, borderRadius: 8, overflow: 'hidden', cursor: f.preview ? 'pointer' : 'default', border: '2px dashed #C7D2FE', background: '#EEF2FF' }}
        >
          {f.preview && isImage ? (
            <img src={f.preview} alt={f.name} style={{ width: '100%', height: '100%', objectFit: 'cover' }} />
          ) : isVideo ? (
            <div style={{ height: '100%', background: 'linear-gradient(135deg,#667eea,#764ba2)', display: 'flex', alignItems: 'center', justifyContent: 'center' }}>
              <FeatherIcon icon="video" size={24} color="white" />
            </div>
          ) : (
            <div style={{ height: '100%', display: 'flex', alignItems: 'center', justifyContent: 'center' }}>
              <FeatherIcon icon="file" size={24} color="#4F46E5" />
            </div>
          )}
          <div style={{ position: 'absolute', bottom: 0, left: 0, right: 0, background: 'linear-gradient(to top,rgba(0,0,0,0.65),transparent)', padding: '4px 5px' }}>
            <span style={{ color: 'white', fontSize: '0.62rem', display: 'block', whiteSpace: 'nowrap', overflow: 'hidden', textOverflow: 'ellipsis' }}>{f.name}</span>
          </div>
          <div style={{ position: 'absolute', top: 4, left: 4 }}>
            <span style={{ background: '#D97706', color: 'white', borderRadius: 4, padding: '1px 5px', fontSize: '0.58rem', fontWeight: 700 }}>pending</span>
          </div>
          {canEdit && (
            <button
              type="button" disabled={disabled}
              onClick={(e) => { e.stopPropagation(); onRemovePending(i); }}
              style={{ position: 'absolute', top: 4, right: 4, width: 22, height: 22, borderRadius: '50%', background: '#DC2626', border: 'none', cursor: 'pointer', display: 'flex', alignItems: 'center', justifyContent: 'center', boxShadow: '0 1px 4px rgba(0,0,0,0.35)' }}
            >
              <FeatherIcon icon="trash-2" size={11} color="white" />
            </button>
          )}
        </div>
      );
    })}
  </div>
);

const DropZone = ({ onFiles, disabled, accept, label }) => {
  const inputRef = useRef();
  const [dragging, setDragging] = useState(false);

  const handleDrop = (e) => {
    e.preventDefault(); setDragging(false);
    if (disabled) return;
    const files = Array.from(e.dataTransfer.files);
    if (files.length) onFiles(files);
  };

  return (
    <div
      onDragOver={(e) => { e.preventDefault(); if (!disabled) setDragging(true); }}
      onDragLeave={() => setDragging(false)}
      onDrop={handleDrop}
      onClick={() => !disabled && inputRef.current?.click()}
      style={{
        border: `2px dashed ${dragging ? '#4F46E5' : '#C7D2FE'}`, borderRadius: 10,
        padding: '16px 20px', cursor: disabled ? 'not-allowed' : 'pointer',
        background: dragging ? '#EEF2FF' : '#F8FAFC', textAlign: 'center', transition: 'all 0.15s',
      }}
    >
      <input ref={inputRef} type="file" style={{ display: 'none' }} accept={accept} multiple
        onChange={(e) => { if (e.target.files?.length) onFiles(Array.from(e.target.files)); e.target.value = ''; }} />
      <FeatherIcon icon="upload-cloud" size={22} color={dragging ? '#4F46E5' : '#94A3B8'} style={{ marginBottom: 4 }} />
      <div style={{ fontSize: '0.82rem', color: '#64748B', fontWeight: 500 }}>{label}</div>
      <div style={{ fontSize: '0.71rem', color: '#94A3B8', marginTop: 2 }}>Click or drag & drop</div>
    </div>
  );
};

const ReportFileThumbnail = ({ fileKey, signedUrl, onRemove, onPreview, canEdit, disabled }) => {
  const type = getFileTypeFromKey(fileKey);
  const fileName = fileKey.split('/').pop();
  const isImage = type === 'image';
  return (
    <div
      onClick={() => signedUrl && onPreview()}
      style={{
        position: 'relative', height: 72, borderRadius: 8, overflow: 'hidden',
        cursor: signedUrl ? 'pointer' : 'default', border: '2px solid #C7D2FE',
        background: '#EEF2FF', display: 'flex', alignItems: 'center',
      }}
    >
      {signedUrl && isImage ? (
        <img src={signedUrl} alt={fileName} style={{ width: '100%', height: '100%', objectFit: 'cover' }} />
      ) : (
        <div style={{ display: 'flex', alignItems: 'center', gap: 8, padding: '0 10px', width: '100%' }}>
          <FeatherIcon icon="file-text" size={18} color="#4F46E5" style={{ flexShrink: 0 }} />
          <span style={{ fontSize: '0.72rem', color: '#4F46E5', fontWeight: 600, overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}>
            {fileName}
          </span>
        </div>
      )}
      <div style={{ position: 'absolute', top: 4, left: 4 }}>
        <span style={{ background: '#059669', color: 'white', borderRadius: 4, padding: '1px 5px', fontSize: '0.58rem', fontWeight: 700 }}>✓</span>
      </div>
      {canEdit && (
        <button
          type="button" disabled={disabled}
          onClick={(e) => { e.stopPropagation(); onRemove(); }}
          style={{ position: 'absolute', top: 4, right: 4, width: 20, height: 20, borderRadius: '50%', background: '#DC2626', border: 'none', cursor: 'pointer', display: 'flex', alignItems: 'center', justifyContent: 'center' }}
        >
          <FeatherIcon icon="trash-2" size={10} color="white" />
        </button>
      )}
    </div>
  );
};

// ─── Main Modal ──────────────────────────────────────────────────────────────
const VIRReportFormModal = ({
  isOpen, onClose, onSave, reportData, isNew,
  properties, categoryOptions, userRole,
  prefilledPropertyId, prefilledBuildingName, prefilledPropertyLabel,
}) => {
  const canEdit = ['company', 'manager', 'technical staff', 'vendor'].includes((userRole || '').toLowerCase());

  const [form, setForm] = useState({});
  const [saving, setSaving] = useState(false);
  const [savedId, setSavedId] = useState(null);
  const [errors, setErrors] = useState({});

  const [vendorOptions, setVendorOptions] = useState([]);
  const [vendorsLoading, setVendorsLoading] = useState(false);

  // Attachment state
  const [uploadedKeys, setUploadedKeys] = useState([]);
  const [pendingFiles, setPendingFiles] = useState([]);

  // Report files state (multiple)
  const [uploadedReportFileKeys, setUploadedReportFileKeys] = useState([]);
  const [pendingReportFiles, setPendingReportFiles] = useState([]);
  const [reportFileSignedUrls, setReportFileSignedUrls] = useState({});

  const [signedUrlCache, setSignedUrlCache] = useState({});
  const fetchedKeysRef = useRef(new Set());

  // Preview state
  const [previewOpen, setPreviewOpen] = useState(false);
  const [previewIndex, setPreviewIndex] = useState(0);
  const [reportFilePreviewOpen, setReportFilePreviewOpen] = useState(false);
  const [reportFilePreviewKey, setReportFilePreviewKey] = useState('');

  // ── Reset on open ──────────────────────────────────────────────────────────
  useEffect(() => {
    if (!isOpen) return;
    if (reportData && !isNew) {
      setForm({
        propertyId: reportData.property?.propertyId?._id || reportData.property?.propertyId || '',
        buildingName: reportData.property?.buildingName || '',
        categoryId: reportData.categoryId?._id || reportData.categoryId || '',
        vendorId: reportData.vendorId?._id || reportData.vendorId || '',
        status: reportData.status || 'Open',
        comments: reportData.comments || '',
        inspectionDate: reportData.inspectionDate
          ? new Date(reportData.inspectionDate).toISOString().split('T')[0]
          : new Date().toISOString().split('T')[0],
      });
      setSavedId(reportData._id);
      setUploadedKeys(Array.isArray(reportData.attachments) ? reportData.attachments : []);
      const existingReportFiles = Array.isArray(reportData.reportFiles)
        ? reportData.reportFiles
        : reportData.reportFile && reportData.reportFile.trim() !== ''
          ? [reportData.reportFile]
          : [];
      setUploadedReportFileKeys(existingReportFiles);
    } else {
      setForm({
        propertyId: prefilledPropertyId || '',
        buildingName: prefilledBuildingName || '',
        categoryId: '',
        vendorId: '',
        status: 'Open',
        comments: '',
        inspectionDate: new Date().toISOString().split('T')[0],
      });
      setSavedId(null);
      setUploadedKeys([]);
      setUploadedReportFileKeys([]);
    }
    setPendingFiles([]);
    setPendingReportFiles([]);
    setErrors({});
    setSignedUrlCache({});
    setReportFileSignedUrls({});
    setVendorOptions([]);
    fetchedKeysRef.current = new Set();
  }, [reportData, isNew, isOpen, prefilledPropertyId, prefilledBuildingName]);

  // ── Fetch vendors when category changes ────────────────────────────────────
  useEffect(() => {
    if (!isOpen) return;
    const catId = form.categoryId;
    if (!catId) { setVendorOptions([]); return; }
    setVendorsLoading(true);
    getVIRVendorsByCategory(catId)
      .then((res) => {
        const opts = (res?.data || []).map((v) => ({
          value: v._id,
          label: v.companyName,
          primaryContact: v.representatives && v.representatives.length > 0
            ? [v.representatives[0].contactName, v.representatives[0].title].filter(Boolean).join(' · ')
            : '',
        }));
        setVendorOptions(opts);
      })
      .catch(() => toast.error('Failed to load vendors for this category'))
      .finally(() => setVendorsLoading(false));
  }, [form.categoryId, isOpen]);

  // ── Fetch signed URLs for uploaded attachments ─────────────────────────────
  useEffect(() => {
    if (!uploadedKeys.length) return;
    const missing = uploadedKeys.filter((k) => !signedUrlCache[k] && !fetchedKeysRef.current.has(k));
    if (!missing.length) return;
    missing.forEach((k) => fetchedKeysRef.current.add(k));
    getVIRSignedUrls(missing)
      .then((urls) => setSignedUrlCache((prev) => ({ ...prev, ...urls })))
      .catch(() => toast.error('Some previews could not be loaded'));
  }, [uploadedKeys, signedUrlCache]);

  // ── Fetch signed URLs for report files ────────────────────────────────────
  useEffect(() => {
    if (!uploadedReportFileKeys.length) return;
    const missing = uploadedReportFileKeys.filter((k) => !reportFileSignedUrls[k]);
    if (!missing.length) return;
    getVIRSignedUrls(missing)
      .then((urls) => setReportFileSignedUrls((prev) => ({ ...prev, ...urls })))
      .catch(() => toast.error('Could not load report file previews'));
  }, [uploadedReportFileKeys]);

  useEffect(() => {
    return () => { pendingFiles.forEach((f) => f.preview && URL.revokeObjectURL(f.preview)); };
  }, [pendingFiles]);

  const carouselItems = useMemo(() => {
    const uploaded = uploadedKeys.map((key) => ({
      src: signedUrlCache[key] || '',
      caption: key.split('/').pop() || key,
      type: getFileTypeFromKey(key),
    }));
    const pending = pendingFiles.map((f) => ({
      src: f.preview || '',
      caption: f.name,
      type: f.type?.startsWith('image/') ? 'image' : f.type?.startsWith('video/') ? 'video' : getFileTypeFromKey(f.name),
    }));
    return [...uploaded, ...pending];
  }, [uploadedKeys, pendingFiles, signedUrlCache]);

  const openPreview = (index) => { setPreviewIndex(index); setPreviewOpen(true); };

  const handleAttachmentFiles = (files) => {
    const entries = files.map((file) => ({
      file,
      name: file.name,
      type: file.type,
      preview: file.type.startsWith('image/') ? URL.createObjectURL(file) : null,
    }));
    setPendingFiles((prev) => [...prev, ...entries]);
  };

  const handleReportFiles = (files) => {
    const entries = files.map((file) => ({ file, name: file.name, type: file.type }));
    setPendingReportFiles((prev) => [...prev, ...entries]);
  };

  const handleRemoveUploaded = async (index) => {
    const result = await Swal.fire({
      title: 'Remove attachment?',
      text: 'This will remove it from the report.',
      icon: 'warning', showCancelButton: true,
      confirmButtonColor: '#DC2626', confirmButtonText: 'Remove',
      cancelButtonText: 'Cancel', width: 380,
    });
    if (!result.isConfirmed) return;
    setUploadedKeys((prev) => prev.filter((_, i) => i !== index));
  };

  const handleRemovePending = (index) => {
    setPendingFiles((prev) => {
      const f = prev[index];
      if (f?.preview) URL.revokeObjectURL(f.preview);
      return prev.filter((_, i) => i !== index);
    });
  };

  const handleRemoveUploadedReportFile = async (index) => {
    const result = await Swal.fire({
      title: 'Remove report file?',
      text: 'This will remove it from the report.',
      icon: 'warning', showCancelButton: true,
      confirmButtonColor: '#DC2626', confirmButtonText: 'Remove',
      cancelButtonText: 'Cancel', width: 380,
    });
    if (!result.isConfirmed) return;
    setUploadedReportFileKeys((prev) => prev.filter((_, i) => i !== index));
  };

  const handleRemovePendingReportFile = (index) => {
    setPendingReportFiles((prev) => prev.filter((_, i) => i !== index));
  };

  const validate = () => {
    const errs = {};
    if (!form.propertyId) errs.propertyId = 'Property is required';
    if (!form.buildingName?.trim()) errs.buildingName = 'Building name is required';
    if (!form.categoryId) errs.categoryId = 'Category is required';
    if (!form.vendorId) errs.vendorId = 'Vendor is required';
    if (!form.status) errs.status = 'Status is required';
    if (!form.inspectionDate) errs.inspectionDate = 'Inspection date is required';
    return errs;
  };

  const uploadPendingFiles = async (reportId) => {
    const tasks = [];
    for (const f of pendingReportFiles) {
      tasks.push(async () => {
        const { signedUrl, key } = await requestVIRUploadUrl(reportId, f.name, f.type, 'report');
        await fetch(signedUrl, { method: 'PUT', body: f.file, headers: { 'Content-Type': f.type } });
        await confirmVIRUpload(reportId, key, 'report');
      });
    }
    for (const f of pendingFiles) {
      tasks.push(async () => {
        const { signedUrl, key } = await requestVIRUploadUrl(reportId, f.name, f.type, 'attachment');
        await fetch(signedUrl, { method: 'PUT', body: f.file, headers: { 'Content-Type': f.type } });
        await confirmVIRUpload(reportId, key, 'attachment');
      });
    }
    await Promise.all(tasks.map((fn) => fn()));
  };

  const set = (k) => (v) => {
    setForm((p) => {
      const next = { ...p, [k]: v };
      if (k === 'categoryId') next.vendorId = '';
      return next;
    });
    if (errors[k]) setErrors((e) => ({ ...e, [k]: undefined }));
  };

  const handleSave = async () => {
    const errs = validate();
    if (Object.keys(errs).length) { setErrors(errs); toast.error('Please fill in all required fields'); return; }

    setSaving(true);
    try {
      const payload = { ...form, attachments: uploadedKeys, reportFiles: uploadedReportFileKeys };
      const result = await onSave(payload, isNew, savedId);
      const newId = result?.data?._id || result?._id;

      if (newId) setSavedId(newId);

      if (newId && (pendingReportFiles.length > 0 || pendingFiles.length > 0)) {
        await uploadPendingFiles(newId);
        setPendingFiles([]);
        setPendingReportFiles([]);
      }
      onClose();
    } catch {
      // handled in API layer
    } finally {
      setSaving(false);
    }
  };

  const selectedCategory = (categoryOptions || []).find((o) => o.value === form.categoryId) || null;
  const selectedVendor = vendorOptions.find((o) => o.value === form.vendorId) || null;
  const selectedStatus = VIR_STATUSES.find((s) => s.value === form.status) || VIR_STATUSES[0];
  const totalAttachments = uploadedKeys.length + pendingFiles.length;
  const totalReportFiles = uploadedReportFileKeys.length + pendingReportFiles.length;

  const displayPropertyLabel = isNew
    ? (prefilledPropertyLabel || prefilledPropertyId || '')
    : (() => { const p = (properties || []).find((p) => p._id === form.propertyId); return p ? (p.title || p.name || form.propertyId) : form.propertyId; })();

  const displayInspectionDate = formatDateDenver(form.inspectionDate);

  return (
    <>
      <Modal isOpen={isOpen} toggle={onClose} centered size="lg">
        <ModalHeader
          toggle={onClose}
          close={
            <button
              type="button"
              onClick={onClose}
              onMouseEnter={e => {
                e.currentTarget.style.background = 'rgba(255,255,255,0.15)';
                e.currentTarget.style.opacity = '1';
                e.currentTarget.style.transform = 'translateY(-50%) rotate(90deg)';
              }}
              onMouseLeave={e => {
                e.currentTarget.style.background = 'rgba(255,255,255,0.07)';
                e.currentTarget.style.opacity = '0.75';
                e.currentTarget.style.transform = 'translateY(-50%) rotate(0deg)';
              }}
              aria-label="Close"
              style={{
                position: 'absolute',
                right: '16px',
                top: '50%',
                transform: 'translateY(-50%)',
                background: 'rgba(255,255,255,0.07)',
                border: '1px solid rgba(255,255,255,0.15)',
                borderRadius: '50%',
                width: '32px',
                height: '32px',
                cursor: 'pointer',
                color: 'white',
                opacity: 0.75,
                fontSize: '1rem',
                display: 'flex',
                alignItems: 'center',
                justifyContent: 'center',
                transition: 'all 0.2s ease',
                backdropFilter: 'blur(4px)',
                lineHeight: 1,
              }}
            >
              ✕
            </button>
          }
          style={{
            background: 'linear-gradient(135deg,#0F172A 0%,#1E293B 100%)',
            borderRadius: '10px 10px 0 0',
            padding: '16px 24px',
            borderBottom: '2px solid #4F46E5',
            position: 'relative',
          }}
        >
          <div style={{ display: 'flex', alignItems: 'center', gap: 10 }}>
            <div style={{ width: 32, height: 32, borderRadius: 8, background: 'rgba(99,102,241,0.25)', display: 'flex', alignItems: 'center', justifyContent: 'center' }}>
              <FeatherIcon icon="file-text" size={16} color="#A5B4FC" />
            </div>
            <div>
              <div style={{ fontWeight: 800, fontSize: '0.95rem', color: 'white' }}>
                {isNew ? 'New Vendor Inspection Report' : 'Edit Report'}
              </div>
              <div style={{ fontSize: '0.72rem', color: 'rgba(165,180,252,0.6)', marginTop: 1 }}>
                {isNew
                  ? `${displayPropertyLabel}${form.buildingName ? ` · ${form.buildingName}` : ''}`
                  : `Editing report · ${displayPropertyLabel}${form.buildingName ? ` · ${form.buildingName}` : ''}`}
              </div>
              <div style={{ fontSize: '0.72rem', color: 'rgba(165,180,252,0.6)', marginTop: 1 }}>
                {isNew
                  ? `${displayPropertyLabel} ${displayInspectionDate}`
                  : `Date · ${displayInspectionDate || ''}`}
              </div>
            </div>
          </div>
        </ModalHeader>

        <ModalBody style={{ padding: '24px', background: '#F8FAFC' }}>
          <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: 16 }}>

            {/* Category */}
            <FormGroup style={{ marginBottom: 0 }}>
              <Label style={LABEL_STYLE}>Category *</Label>
              <Select
                options={categoryOptions || []} value={selectedCategory}
                onChange={(opt) => set('categoryId')(opt?.value || '')}
                placeholder="Select category..."
                styles={{ ...selectStyles, control: (base, state) => ({ ...selectStyles.control(base, state), borderColor: errors.categoryId ? '#DC2626' : state.isFocused ? '#4F46E5' : '#E2E8F0' }) }}
                isDisabled={!canEdit} menuPortalTarget={document.body} menuPosition="fixed"
              />
              <FieldError msg={errors.categoryId} />
            </FormGroup>

            {/* Vendor */}
            <FormGroup style={{ marginBottom: 0 }}>
              <Label style={LABEL_STYLE}>Vendor *</Label>
              <Select
                options={vendorOptions}
                value={selectedVendor}
                onChange={(opt) => set('vendorId')(opt?.value || '')}
                placeholder={
                  !form.categoryId
                    ? 'Select a category first...'
                    : vendorsLoading
                      ? 'Loading vendors...'
                      : vendorOptions.length === 0
                        ? 'No vendors for this category'
                        : 'Select vendor...'
                }
                isLoading={vendorsLoading}
                isDisabled={!canEdit || !form.categoryId || vendorsLoading}
                formatOptionLabel={(opt) => (
                  <div>
                    <div style={{ fontWeight: 700, fontSize: '0.83rem', color: '#0F172A' }}>{opt.label}</div>
                    {opt.primaryContact && (
                      <div style={{ fontSize: '0.72rem', color: '#64748B', marginTop: 1 }}>
                        {opt.primaryContact}
                      </div>
                    )}
                  </div>
                )}
                styles={{
                  ...selectStyles,
                  control: (base, state) => ({
                    ...selectStyles.control(base, state),
                    borderColor: errors.vendorId ? '#DC2626' : state.isFocused ? '#4385ff' : '#E2E8F0',
                  }),
                }}
                menuPortalTarget={document.body}
                menuPosition="fixed"
              />
              <FieldError msg={errors.vendorId} />
              {form.categoryId && !vendorsLoading && vendorOptions.length === 0 && (
                <div style={{ fontSize: '0.72rem', color: '#D97706', marginTop: 4, display: 'flex', alignItems: 'center', gap: 4 }}>
                  <FeatherIcon icon="alert-triangle" size={11} />
                  No active vendors serve this category. Add one in the Vendors tab.
                </div>
              )}
            </FormGroup>

            {/* Status */}
            <FormGroup style={{ marginBottom: 0 }}>
              <Label style={LABEL_STYLE}>Status *</Label>
              <Select
                options={VIR_STATUSES} value={selectedStatus}
                onChange={(opt) => set('status')(opt?.value || 'Open')}
                styles={selectStyles} isDisabled={!canEdit}
                menuPortalTarget={document.body} menuPosition="fixed"
                formatOptionLabel={(opt) => (
                  <div style={{ display: 'flex', alignItems: 'center', gap: 8 }}>
                    <div style={{ width: 8, height: 8, borderRadius: '50%', background: opt.color, flexShrink: 0 }} />
                    {opt.label}
                  </div>
                )}
              />
              <FieldError msg={errors.status} />
            </FormGroup>

            {/* Inspection Date */}
            <FormGroup style={{ marginBottom: 0 }}>
              <Label style={LABEL_STYLE}>Inspection Date *</Label>
              <Input
                type="date"
                value={form.inspectionDate || ''}
                onChange={(e) => set('inspectionDate')(e.target.value)}
                disabled={!canEdit}
                max={new Date().toISOString().split('T')[0]}
                style={{ borderColor: errors.inspectionDate ? '#DC2626' : '#E2E8F0', borderRadius: 8, fontSize: '0.83rem' }}
              />
              <FieldError msg={errors.inspectionDate} />
            </FormGroup>



            {/* General Comments */}
            <FormGroup style={{ gridColumn: '1 / -1', marginBottom: 0 }}>
              <Label style={LABEL_STYLE}>Comments / Notes</Label>
              <Input
                type="textarea" rows={3} value={form.comments || ''}
                onChange={(e) => set('comments')(e.target.value)}
                placeholder="Additional comments or notes..."
                disabled={!canEdit}
                style={{ borderColor: '#E2E8F0', borderRadius: 8, fontSize: '0.83rem', resize: 'vertical' }}
              />
            </FormGroup>

            {/* ── File uploads (editors only) ─────────────────────────── */}
            {canEdit && (
              <>
                {/* Report files (multiple) */}
                <FormGroup style={{ gridColumn: '1 / -1', marginBottom: 0 }}>
                  <Label style={LABEL_STYLE}>
                    Inspection Report Files
                    {totalReportFiles > 0 && (
                      <span style={{ marginLeft: 6, background: '#4F46E5', color: 'white', borderRadius: 10, padding: '1px 7px', fontSize: '0.68rem', fontWeight: 700 }}>
                        {totalReportFiles}
                      </span>
                    )}
                  </Label>

                  <DropZone
                    onFiles={handleReportFiles}
                    accept=".pdf,.doc,.docx,.xls,.xlsx"
                    label="Upload Report Files (PDF, Word, Excel)"
                    disabled={saving}
                  />

                  {uploadedReportFileKeys.length > 0 && (
                    <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fill, minmax(220px, 1fr))', gap: 8, marginTop: 10 }}>
                      {uploadedReportFileKeys.map((key, i) => (
                        <ReportFileThumbnail
                          key={`rf-up-${i}`}
                          fileKey={key}
                          signedUrl={reportFileSignedUrls[key]}
                          onRemove={() => handleRemoveUploadedReportFile(i)}
                          onPreview={() => { setReportFilePreviewKey(key); setReportFilePreviewOpen(true); }}
                          canEdit={canEdit}
                          disabled={saving}
                        />
                      ))}
                    </div>
                  )}

                  {pendingReportFiles.length > 0 && (
                    <div style={{ display: 'flex', flexDirection: 'column', gap: 6, marginTop: 8 }}>
                      {pendingReportFiles.map((f, i) => (
                        <div key={`rf-pend-${i}`} style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', background: '#EEF2FF', border: '1px solid #C7D2FE', borderRadius: 8, padding: '7px 12px', fontSize: '0.78rem' }}>
                          <div style={{ display: 'flex', alignItems: 'center', gap: 6 }}>
                            <FeatherIcon icon="file-text" size={13} color="#4F46E5" />
                            <span style={{ color: '#4F46E5', fontWeight: 600 }}>{f.name}</span>
                            <span style={{ background: '#D97706', color: 'white', borderRadius: 4, padding: '1px 5px', fontSize: '0.58rem', fontWeight: 700 }}>pending</span>
                          </div>
                          <button type="button" disabled={saving} onClick={() => handleRemovePendingReportFile(i)}
                            style={{ background: 'none', border: 'none', cursor: 'pointer', padding: 2, display: 'flex', alignItems: 'center' }}>
                            <FeatherIcon icon="trash-2" size={13} color="#DC2626" />
                          </button>
                        </div>
                      ))}
                    </div>
                  )}

                  {pendingReportFiles.length > 0 && (
                    <div style={{ marginTop: 6, padding: '6px 12px', background: '#EFF6FF', border: '1px solid #BFDBFE', borderRadius: 7, fontSize: '0.75rem', color: '#1D4ED8', display: 'flex', alignItems: 'center', gap: 6 }}>
                      <FeatherIcon icon="cloud" size={13} />
                      {pendingReportFiles.length} report file{pendingReportFiles.length !== 1 ? 's' : ''} pending — will upload when you save
                    </div>
                  )}
                </FormGroup>

                {/* Attachments */}
                <FormGroup style={{ gridColumn: '1 / -1', marginBottom: 0 }}>
                  <Label style={LABEL_STYLE}>
                    Photos / Attachments
                    {totalAttachments > 0 && (
                      <span style={{ marginLeft: 6, background: '#4F46E5', color: 'white', borderRadius: 10, padding: '1px 7px', fontSize: '0.68rem', fontWeight: 700 }}>
                        {totalAttachments}
                      </span>
                    )}
                  </Label>

                  <DropZone
                    onFiles={handleAttachmentFiles}
                    accept="image/*,.pdf,video/mp4,video/quicktime,video/webm"
                    label="Upload Images, Videos or PDFs"
                    disabled={saving}
                  />

                  {totalAttachments > 0 && (
                    <ThumbnailGrid
                      uploadedKeys={uploadedKeys}
                      pendingFiles={pendingFiles}
                      signedUrlCache={signedUrlCache}
                      onPreview={openPreview}
                      onRemoveUploaded={handleRemoveUploaded}
                      onRemovePending={handleRemovePending}
                      canEdit={canEdit}
                      disabled={saving}
                    />
                  )}

                  {pendingFiles.length > 0 && (
                    <div style={{ marginTop: 8, padding: '7px 12px', background: '#EFF6FF', border: '1px solid #BFDBFE', borderRadius: 7, fontSize: '0.75rem', color: '#1D4ED8', display: 'flex', alignItems: 'center', gap: 6 }}>
                      <FeatherIcon icon="cloud" size={13} />
                      {pendingFiles.length} file{pendingFiles.length !== 1 ? 's' : ''} pending — will upload when you save
                    </div>
                  )}
                </FormGroup>
              </>
            )}

            {/* Read-only attachment view */}
            {!canEdit && uploadedKeys.length > 0 && (
              <FormGroup style={{ gridColumn: '1 / -1', marginBottom: 0 }}>
                <Label style={LABEL_STYLE}>
                  Photos / Attachments
                  <span style={{ marginLeft: 6, background: '#4F46E5', color: 'white', borderRadius: 10, padding: '1px 7px', fontSize: '0.68rem', fontWeight: 700 }}>
                    {uploadedKeys.length}
                  </span>
                </Label>
                <ThumbnailGrid
                  uploadedKeys={uploadedKeys} pendingFiles={[]}
                  signedUrlCache={signedUrlCache} onPreview={openPreview}
                  onRemoveUploaded={() => { }} onRemovePending={() => { }}
                  canEdit={false} disabled={false}
                />
              </FormGroup>
            )}

          </div>
        </ModalBody>

        <ModalFooter style={{ background: 'white', padding: '14px 24px', gap: 8, borderTop: '1px solid #E2E8F0' }}>
          <button type="button" className="it-btn it-btn--cancel" onClick={onClose} disabled={saving}>
            <FeatherIcon icon="x" size={13} /> Cancel
          </button>
          {canEdit && (
            <button
              type="button"
              className={`it-btn it-btn--save${saving ? ' it-btn--saving' : ''}`}
              onClick={handleSave} disabled={saving} style={{ minWidth: 130 }}
            >
              {saving ? <Spinner size="sm" /> : <FeatherIcon icon="check" size={13} />}
              {saving ? 'Saving...' : isNew ? 'Create Report' : 'Save Changes'}
            </button>
          )}
        </ModalFooter>
      </Modal>

      <AttachmentPreviewModal
        isOpen={previewOpen}
        toggle={() => setPreviewOpen(false)}
        items={carouselItems}
        initialIndex={previewIndex}
      />

      <ReportFilePreviewModal
        isOpen={reportFilePreviewOpen}
        toggle={() => setReportFilePreviewOpen(false)}
        signedUrl={reportFileSignedUrls[reportFilePreviewKey]}
        fileKey={reportFilePreviewKey}
      />
    </>
  );
};

export { VIRReportFormModal };